// BalanceCard.jsx - With perfectly matched spacing to Your Balances
import React, { useMemo, useState, useEffect } from 'react';
import { formatBalance } from '../utils/formatters';
import tokenPriceStore from '../services/tokenPriceStore'; // Import the tokenPriceStore directly

const BalanceCard = ({ 
  walletEthBalance = "0", 
  walletTokenBalance = "0", 
  fundEthBalance = "0", 
  fundTokenBalance = "0", 
  nav = "0", 
  contractNav = "0",
  calculatedNav = "0",
  tokenPrice = "0",
  totalSupply = "0",
  tokenPrices = {},
  unallocatedEthBalance = "0"
}) => {
  // State for flipping the card
  const [showDetails, setShowDetails] = useState(false);
  // State to store our own ETH price from tokenPriceStore
  const [storeEthPrice, setStoreEthPrice] = useState(0);
  
  // List of known Ethereum addresses in priority order
  const KNOWN_ETH_ADDRESSES = [
    "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2".toLowerCase(), // WETH on Ethereum (main priority)
    "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619".toLowerCase(), // Polygon ETH
    "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee".toLowerCase(), // Generic ETH address
    "0x0000000000000000000000000000000000000000".toLowerCase(), // Sometimes used for native ETH
    "0x4200000000000000000000000000000000000006".toLowerCase()  // WETH on Optimism
  ];

  // Effect to load ETH price directly from tokenPriceStore
  useEffect(() => {
    // Function to get ETH price from tokenPriceStore
    const getEthPriceFromStore = () => {
      // Try each ETH address in priority order
      for (const ethAddr of KNOWN_ETH_ADDRESSES) {
        const price = tokenPriceStore.getPrice(ethAddr);
        if (price && price > 0) {
          console.log(`Found ETH price from tokenPriceStore for ${ethAddr}: ${price}`);
          setStoreEthPrice(price);
          return;
        }
      }
    };

    // Get price initially
    getEthPriceFromStore();

    // Set up listener for price updates
    const handlePriceUpdate = () => {
      getEthPriceFromStore();
    };

    // Add listener to tokenPriceStore
    tokenPriceStore.addListener(handlePriceUpdate);

    // Clean up listener when component unmounts
    return () => {
      tokenPriceStore.removeListener(handlePriceUpdate);
    };
  }, []);
  
  // Get ETH price using improved detection for consistency across browsers
  const ethPrice = useMemo(() => {
    // 1. First priority: Use storeEthPrice from our useEffect if available
    if (storeEthPrice > 0) {
      return storeEthPrice;
    }
    
    // 2. Second priority: Try each known ETH address in tokenPrices prop
    for (const ethAddr of KNOWN_ETH_ADDRESSES) {
      if (tokenPrices[ethAddr] && tokenPrices[ethAddr] > 0) {
        console.log(`Found ETH price using known address ${ethAddr}: ${tokenPrices[ethAddr]}`);
        return tokenPrices[ethAddr];
      }
    }
    
    // 3. Look in tokenPrices for any key containing "eth" but NOT containing "beth", "seth", etc.
    if (tokenPrices && Object.keys(tokenPrices).length > 0) {
      const ethKeyMatch = Object.keys(tokenPrices).find(key => {
        const keyLower = key.toLowerCase();
        return (
          (keyLower.includes("eth") || keyLower.includes("ether")) && 
          !keyLower.includes("beth") && 
          !keyLower.includes("seth") && 
          !keyLower.includes("meth") &&
          !keyLower.includes("teeth") &&
          !keyLower.includes("crypto") &&
          !keyLower.includes("synth")
        );
      });
      
      if (ethKeyMatch && tokenPrices[ethKeyMatch] > 0) {
        console.log(`Found ETH price by name match: ${ethKeyMatch}: ${tokenPrices[ethKeyMatch]}`);
        return tokenPrices[ethKeyMatch];
      }
    }
    
    // 4. If we haven't found a price yet, try a range heuristic as last resort
    if (tokenPrices && Object.keys(tokenPrices).length > 0) {
      // Filter for tokens that are likely to be ETH by price (between $1500-5000)
      const ethPriceRange = Object.entries(tokenPrices).filter(([, price]) => 
        price >= 1500 && price <= 5000
      );
      
      if (ethPriceRange.length > 0) {
        console.log(`Found potential ETH by price range: ${ethPriceRange[0][1]}`);
        return ethPriceRange[0][1];
      }
    }
    
    // 5. Last resort: Use a hard-coded reasonable default for ETH
    console.log("Using default ETH price: $3200");
    return 3200; // A reasonable default ETH price (as of April 2025)
  }, [tokenPrices, storeEthPrice]);
  
  // Calculate which NAV to display with a clear explanation
  const { displayNav, navSource, navDiscrepancy } = useMemo(() => {
    const contractNavValue = parseFloat(contractNav || "0");
    const calculatedNavValue = parseFloat(calculatedNav || "0");
    
    // Default to calculated NAV if it's valid and larger
    if (calculatedNavValue > 0 && calculatedNavValue > contractNavValue) {
      return {
        displayNav: calculatedNav,
        navSource: "calculated",
        navDiscrepancy: Math.abs(calculatedNavValue - contractNavValue)
      };
    }
    
    // Fall back to contract NAV
    return {
      displayNav: contractNav,
      navSource: "contract",
      navDiscrepancy: Math.abs(calculatedNavValue - contractNavValue)
    };
  }, [contractNav, calculatedNav]);
  
  // Calculate token price based on NAV and total supply
  const calculatedTokenPrice = useMemo(() => {
    if (parseFloat(totalSupply) > 0 && parseFloat(displayNav) > 0) {
      return parseFloat(displayNav) / parseFloat(totalSupply);
    }
    return parseFloat(tokenPrice);
  }, [displayNav, totalSupply, tokenPrice]);
  
  // Calculate USD values (with safety checks)
  const navUsd = parseFloat(displayNav) * ethPrice;
  const contractNavUsd = parseFloat(contractNav) * ethPrice;
  const calculatedNavUsd = parseFloat(calculatedNav) * ethPrice;
  const walletEthUsd = parseFloat(walletEthBalance) * ethPrice;
  const fundEthUsd = parseFloat(fundEthBalance) * ethPrice;
  const unallocatedEthUsd = parseFloat(unallocatedEthBalance) * ethPrice;
  const tokenPriceUsd = calculatedTokenPrice * ethPrice;
  
  // Calculate user ownership percentage and value
  const userOwnershipPercentage = useMemo(() => {
    if (parseFloat(totalSupply) <= 0) return 0;
    
    const userTokens = parseFloat(fundTokenBalance) + parseFloat(walletTokenBalance);
    return (userTokens / parseFloat(totalSupply)) * 100;
  }, [fundTokenBalance, walletTokenBalance, totalSupply]);
  
  // Calculate user's total value in the fund
  const userFundValueUsd = useMemo(() => {
    return navUsd * (userOwnershipPercentage / 100);
  }, [navUsd, userOwnershipPercentage]);
  
  // Calculate wallet token value
  const walletTokenValueUsd = useMemo(() => {
    return parseFloat(walletTokenBalance) * tokenPriceUsd;
  }, [walletTokenBalance, tokenPriceUsd]);
  
  // Calculate fund token value
  const fundTokenValueUsd = useMemo(() => {
    return parseFloat(fundTokenBalance) * tokenPriceUsd;
  }, [fundTokenBalance, tokenPriceUsd]);
  
  // Format the discrepancy percentage for display
  const formatDiscrepancyPercentage = () => {
    const baseValue = Math.max(parseFloat(contractNav), 0.000001); // Avoid division by zero
    const percentage = (navDiscrepancy / baseValue) * 100;
    return percentage > 1 ? percentage.toFixed(1) + '%' : '<1%';
  };
  
  // CSS classes for the flip animation
  const cardFrontClass = `rounded-lg p-4 shadow-[0_2px_4px_1px_rgba(0,0,0,0.1)] bg-white overflow-hidden transition-transform duration-500 
                         ${showDetails ? 'absolute opacity-0 -rotate-y-180' : 'relative opacity-100 rotate-y-0'}`;
  
  const cardBackClass = `rounded-lg p-4 shadow-[0_2px_4px_1px_rgba(0,0,0,0.1)] bg-white overflow-hidden transition-transform duration-500 
                        ${showDetails ? 'relative opacity-100 rotate-y-0' : 'absolute opacity-0 rotate-y-180'}`;

  return (
    <div className="flex flex-col gap-6">
      {/* Your Balances Card */}
      <div className="rounded-lg p-4 shadow-[0_2px_4px_1px_rgba(0,0,0,0.1)] bg-white overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg">Your Balances</h3>
          {userOwnershipPercentage > 0 && (
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              Fund Ownership: {userOwnershipPercentage.toFixed(2)}%
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Wallet</p>
            <p className="mb-1">
              <span className="font-medium">ETH:</span> {formatBalance(walletEthBalance, 4)} ETH
              {ethPrice > 0 && (
                  <span className="text-xs text-gray-500 ml-1">(${walletEthUsd.toFixed(2)})</span>
              )}
            </p>
            <p>
              <span className="font-medium">Fund Tokens:</span> {formatBalance(walletTokenBalance, 4)}
              {tokenPriceUsd > 0 && parseFloat(walletTokenBalance) > 0 && (
                  <span className="text-xs text-gray-500 ml-1">
                    (${walletTokenValueUsd.toFixed(2)})
                  </span>
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">In Fund</p>
            <p className="mb-1">
              <span className="font-medium">ETH Deposit:</span> {formatBalance(fundEthBalance, 4)} ETH
              {ethPrice > 0 && (
                  <span className="text-xs text-gray-500 ml-1">(${fundEthUsd.toFixed(2)})</span>
              )}
            </p>
            <p>
              <span className="font-medium">Fund Tokens:</span> {formatBalance(fundTokenBalance, 4)}
              {tokenPriceUsd > 0 && parseFloat(fundTokenBalance) > 0 && (
                  <span className="text-xs text-gray-500 ml-1">
                    (${fundTokenValueUsd.toFixed(2)})
                  </span>
              )}
            </p>
            
            {/* Show unallocated ETH if available */}
            {parseFloat(unallocatedEthBalance) > 0 && (
              <div className="mt-2 p-2 bg-yellow-50 rounded-lg border border-yellow-100">
                <p className="text-sm text-yellow-800">
                  <span className="font-medium">Unallocated ETH:</span> {formatBalance(unallocatedEthBalance, 4)} ETH
                  {ethPrice > 0 && (
                    <span className="text-xs ml-1">(${unallocatedEthUsd.toFixed(2)})</span>
                  )}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  This ETH is part of your fund ownership and is included in your total value.
                </p>
              </div>
            )}
          </div>
          
          {/* User's total fund value */}
          {userOwnershipPercentage > 0 && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-blue-800">Your Balance:</p>
                <p className="font-bold text-blue-800">${userFundValueUsd.toFixed(2)}</p>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                This represents {userOwnershipPercentage.toFixed(2)}% of the total fund value
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Fund Metrics Card with Flip Animation - EXACTLY MATCHING YOUR BALANCES SPACING */}
      <div className="relative min-h-[180px] perspective-1000">
        {/* Front side of the card - PERFECT MATCH WITH YOUR BALANCES */}
        <div className={cardFrontClass}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-lg">Fund Metrics</h3>
            
            {/* Info Button */}
            <button 
              className="text-blue-500 hover:text-blue-700 focus:outline-none"
              onClick={() => setShowDetails(true)}
              aria-label="Show details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          {/* Exactly matching Your Balances spacing */}
          <div>
            <p className="text-sm text-gray-500 mb-1">Fund Value</p>
            <p className="mb-1">
              <span className="font-medium">Net Asset Value:</span> 
              <span className="font-bold text-blue-800 ml-1">${navUsd.toFixed(2)}</span>
              <span className="text-xs text-gray-500 ml-1">({formatBalance(displayNav, 4)} ETH)</span>
            </p>
            
            <p className="mb-1">
              <span className="font-medium">Token Price:</span> 
              <span className="font-bold text-blue-800 ml-1">${tokenPriceUsd.toFixed(2)}</span>
              <span className="text-xs text-gray-500 ml-1">({formatBalance(calculatedTokenPrice, 6)} ETH)</span>
            </p>
            
            <p>
              <span className="font-medium">Total Minted Tokens:</span> 
              <span className="font-bold text-blue-800 ml-1">{formatBalance(totalSupply, 2)}</span>
            </p>
          </div>

          {/* ETH Price indicator */}
          {ethPrice > 0 && (
            <div className="mt-4 p-2 bg-gray-50 rounded border border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-600">ETH Price:</p>
              <div className="flex items-center">
                <p className="font-medium text-gray-800">${ethPrice.toFixed(2)}</p>
                <div className="ml-2 w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Live price feed"></div>
              </div>
            </div>
          )}
        </div>
        
        {/* Back side of the card (Details) */}
        <div className={cardBackClass}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-lg">Fund Metrics Details</h3>
            
            {/* Back Button */}
            <button 
              className="text-blue-500 hover:text-blue-700 focus:outline-none"
              onClick={() => setShowDetails(false)}
              aria-label="Back to metrics"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          <div className="overflow-y-auto max-h-[300px] pr-1">
            <div className="mb-4">
              <h4 className="font-medium text-blue-700 mb-1">About Net Asset Value (NAV)</h4>
              <p className="text-sm text-gray-600 mb-2">
                The Net Asset Value represents the total value of all tokens held in the fund, expressed in ETH.
                It's calculated by summing the USD value of each token position and dividing by the ETH price.
                The Contract NAV may differ as it's updated based on the last trade, while the Current NAV
                reflects current market prices in real-time.
              </p>
              
              <div className="bg-gray-50 p-2 rounded mb-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Contract NAV:</span>
                  <span className="text-sm">${contractNavUsd.toFixed(2)}
                    <span className="text-xs text-gray-500 ml-1">({formatBalance(contractNav, 4)} ETH)</span>
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm font-medium">Current NAV:</span>
                  <span className="text-sm">${calculatedNavUsd.toFixed(2)}
                    <span className="text-xs text-gray-500 ml-1">({formatBalance(calculatedNav, 4)} ETH)</span>
                  </span>
                </div>
                {navDiscrepancy > 0.01 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Difference: {formatDiscrepancyPercentage()}
                  </p>
                )}
              </div>
              
              <p className="text-xs text-blue-600">
                {navSource === "calculated" 
                  ? "Currently using calculated NAV based on real-time market prices." 
                  : "Currently using NAV from contract."}
              </p>
            </div>
            
            <div className="mb-4">
              <h4 className="font-medium text-blue-700 mb-1">About Token Price</h4>
              <p className="text-sm text-gray-600 mb-2">
                The token price represents the value of a single fund token. It's calculated by dividing the total NAV by the number of tokens (total supply).
              </p>
              <div className="bg-gray-50 p-2 rounded">
                <p className="text-xs text-gray-600">
                  Token Price = Net Asset Value ÷ Total Supply
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {formatBalance(displayNav, 4)} ETH ÷ {formatBalance(totalSupply, 2)} tokens = {formatBalance(calculatedTokenPrice, 6)} ETH per token
                  {ethPrice > 0 && (
                    <span className="ml-1">(${tokenPriceUsd.toFixed(2)})</span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              <h4 className="font-medium text-blue-700 mb-1">About Your Ownership</h4>
              <p className="text-sm text-gray-600 mb-2">
                Your ownership percentage is calculated by dividing your total tokens (both in fund and wallet) by the total supply of tokens.
              </p>
              <div className="bg-gray-50 p-2 rounded">
                <p className="text-xs text-gray-600">
                  Ownership % = (Your Tokens ÷ Total Supply) × 100
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  ({formatBalance(fundTokenBalance, 2)} + {formatBalance(walletTokenBalance, 2)}) ÷ {formatBalance(totalSupply, 2)} × 100 = {userOwnershipPercentage.toFixed(2)}%
                </p>
                {userFundValueUsd > 0 && (
                  <p className="text-xs text-blue-600 mt-1">
                    Your value: ${userFundValueUsd.toFixed(2)} ({userOwnershipPercentage.toFixed(2)}% of ${navUsd.toFixed(2)})
                  </p>
                )}
              </div>
            </div>
            
            {parseFloat(unallocatedEthBalance) > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-blue-700 mb-1">About Unallocated ETH</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Unallocated ETH represents fund ETH that is currently held in the trading reserves. These funds can be used for buying tokens or may temporarily hold ETH after trades.
                </p>
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-xs text-gray-600">
                    Available Unallocated ETH: {formatBalance(unallocatedEthBalance, 4)} ETH (${unallocatedEthUsd.toFixed(2)})
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">
                    This ETH is part of the fund's NAV and your ownership value.
                  </p>
                </div>
              </div>
            )}
            
            <div className="mb-2">
              <h4 className="font-medium text-blue-700 mb-1">About USD Values</h4>
              <p className="text-sm text-gray-600 mb-2">
                All USD values are calculated using the current ETH price from external price feeds:
                ${ethPrice.toFixed(2)} per ETH.
              </p>
              <p className="text-xs text-gray-600">
                These prices are updated automatically every minute to reflect current market conditions.
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setShowDetails(false)}
            className="mt-3 w-full py-2 px-4 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition"
          >
            Back to Fund Metrics
          </button>
        </div>
      </div>
    </div>
  );
};

export default BalanceCard;